import { css, cx } from '@emotion/css';
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigator } from '../../../routing/navigator';
import {
  CheckCircleOutline,
  CheckCircleSolidInverted,
  QuestionmarkCircle,
} from '../../../shared/assets/icons';
import {
  Button,
  ButtonSizeEnum,
  ButtonVariantEnum,
} from '../../../shared/components/Button';
import { Layout } from '../../../shared/components/layout/Layout';
import { Loader } from '../../../shared/components/Loader';
import { useUser } from '../../../shared/providers/user-provider';
import { CacheKeys } from '../../../shared/services/api/cache-configuration';
import { useLocations } from '../../../shared/services/api/location';
import { http } from '../../../shared/services/http';
import { locationStore } from '../../../shared/stores/location-store';
import { Colors } from '../../../shared/styles/colors';
import {
  BOTTOM_NAV_SAFE_HEIGHT,
  HEADER_HEIGHT,
} from '../../../shared/styles/css-values';
import { h1Style, p2Style } from '../../../shared/styles/text';
import { PaymentTypeEnum } from '../../../shared/types/enums';
import { Account, Location } from '../../../shared/types/server-types';
import { showPrivateToast } from '../../../shared/utils/show-private-toast';
import { Motion, Radius, unstyledButton } from '../../../shared/styles/tokens';

export const WalletAddFundsPage: React.FC = () => {
  const navigator = useNavigator();
  const queryClient = useQueryClient();
  const { data: locations, isLoading, isError } = useLocations();
  const user = useUser()!;
  const currentLocation = locationStore.getLocation()!;
  const [selectedLocation, setSelectedLocation] =
    useState<Location>(currentLocation);
  const [creatingAccount, setCreatingAccount] = useState<boolean>(false);

  // A prepaid account is only useful if funds can actually be added, so
  // prepaid locations without a working card gateway are not offered here.
  // Postpaid/periodical locations only create an account — no payment needed.
  const possibleAccountLocations = locations?.filter(
    (location) =>
      !location.parentLocation &&
      (location.hasCardPaymentEnabled ||
        location.constants?.defaultPaymentType !== PaymentTypeEnum.PREPAID),
  );

  const unvisitedLocations = useMemo(() => {
    const visitedLocationsIds = user.accounts.map((account: Account) => {
      return account.location.id;
    });
    return possibleAccountLocations?.filter(
      (location: Location) => !visitedLocationsIds.includes(location.id),
    );
  }, [locations]);

  const handleLocationSelect = async () => {
    if (selectedLocation && !creatingAccount) {
      switch (selectedLocation.constants.defaultPaymentType) {
        case PaymentTypeEnum.PREPAID:
          navigator.toWalletAddFundsToLocation(selectedLocation.id);
          break;
        case PaymentTypeEnum.POSTPAID:
        case PaymentTypeEnum.PERIODICAL:
          try {
            setCreatingAccount(true);
            await http.createAccount(selectedLocation.id);
            await queryClient.refetchQueries({ queryKey: [CacheKeys.ME] });
            navigator.toSuccessful();
          } finally {
            setCreatingAccount(false);
          }
          break;
        default:
          navigator.toPrevious();
          showPrivateToast(
            `Can't create your account right now. Try again later.`,
          );
      }
    }
  };

  if (isError) {
    return <p className={errorStyle}>Something went wrong</p>;
  }

  if (isLoading || !unvisitedLocations) {
    return <Loader />;
  }

  return (
    <Layout.Content>
      <div className={innerContainerStyle}>
        <h1 className={h1Style}>Where do you want to create an account?</h1>
        <p className={cx(p2Style, descriptionStyle)}>
          Accounts are location specific. If you are planning a visit to a
          location, you can add funds to an account in advance or set up an
          postpaid account.
        </p>
        <Button
          startIcon={<QuestionmarkCircle />}
          variant={ButtonVariantEnum.TEXT}
          size={ButtonSizeEnum.MEDIUM}
          text={`more info`}
        />
        <div className={listItemRootStyle}>
          <ul
            className={locationListStyle}
            role="radiogroup"
            aria-label="Location"
          >
            {unvisitedLocations.map((location: Location) => {
              const selected =
                selectedLocation && location.id === selectedLocation?.id;
              return (
                <li key={location.id}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={!!selected}
                    className={cx(
                      listItemActionStyle,
                      selected && listItemSelectedStyle,
                    )}
                    onClick={() => setSelectedLocation(location)}
                  >
                    <span>{location.name}</span>
                    {selected ? (
                      <CheckCircleSolidInverted className={iconStyle} />
                    ) : (
                      <CheckCircleOutline className={iconOutlineStyle} />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          <div>
            <Button
              size={ButtonSizeEnum.LARGE}
              variant={
                selectedLocation
                  ? ButtonVariantEnum.FILLED
                  : ButtonVariantEnum.OUTLINE
              }
              text={selectedLocation ? `create account` : `select location`}
              fullWidth
              gutterbottom
              loading={creatingAccount}
              className={confirmButtonStyle}
              onClick={handleLocationSelect}
            />
          </div>
        </div>
      </div>
      <Button
        text={'back'}
        size={ButtonSizeEnum.LARGE}
        variant={ButtonVariantEnum.TEXT}
        onClick={navigator.toPrevious}
      />
    </Layout.Content>
  );
};

const listItemRootStyle = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const locationListStyle = css`
  list-style: none;
  border: 1px solid ${Colors.BORDER};
  border-radius: ${Radius.md};
  overflow: hidden;

  > li + li {
    border-top: 1px solid ${Colors.BORDER};
  }
`;

const listItemActionStyle = css`
  ${unstyledButton}
  ${p2Style}
  width: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem 1rem;
  transition: background-color ${Motion.fast};

  > span {
    overflow-wrap: anywhere;
  }

  @media (hover: hover) {
    &:hover {
      background: ${Colors.SURFACE};
    }
  }

  &:focus-visible {
    outline: 2px solid ${Colors.CAPRI_BLUE};
    outline-offset: -2px;
  }
`;

const listItemSelectedStyle = css`
  background: ${Colors.CAPRI_BLUE_TINT};
  font-weight: bold;

  @media (hover: hover) {
    &:hover {
      background: ${Colors.CAPRI_BLUE_TINT};
    }
  }
`;

const innerContainerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 1rem;
  margin-top: calc(6.375rem - ${HEADER_HEIGHT});
  min-height: calc(
    100vh - 6.375rem - ${HEADER_HEIGHT} - ${BOTTOM_NAV_SAFE_HEIGHT}
  );
`;

const descriptionStyle = css`
  color: ${Colors.TEXT_SECONDARY};
`;

const confirmButtonStyle = css`
  margin-top: 0.5rem;
`;

const iconStyle = css`
  flex: 0 0 auto;
  fill: ${Colors.CAPRI_BLUE};
`;

const iconOutlineStyle = css`
  flex: 0 0 auto;
`;

const errorStyle = css`
  padding: 2rem 1.5rem;
  color: ${Colors.TEXT_SECONDARY};
`;
